import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  Menu,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Paper,
  Autocomplete,
  Chip,
  Stack,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../services/api';
import testService from '../../services/testService';
import { filterPublished } from '../../utils/publishedFilter';
import ManualTestDialog from '../../components/tests/ManualTestDialog';
import SummaryCard from '../../components/common/SummaryCard';
import ViewTestQuestionsDialog from '../../components/tests/ViewTestQuestionsDialog';
import { 
  SUBJECTS, 
  ACADEMIC_YEARS, 
  BOARDS_EXAMS, 
  SUBJECT_TOPICS,
  DIFFICULTIES,
  TESTS,
  TESTS_GENERATE,
  TESTS_WORKSHEET,
  META_TAGS,
  TEST_TYPES,
  TEST_TYPE_OPTIONS
} from '../../config/Config';
import AutoTestDialog from '../../components/tests/AutoTestDialog';
import TestQuestionReviewDialog from '../../components/tests/TestQuestionReviewDialog';
import { useAuth } from '../../contexts/AuthContext';
import TagSelector from '../../components/forms/TagSelector';
import { processTagPayload } from '../../utils/tagPayload';
import {
  teacherFilterClearButtonSx,
  teacherFilterFieldSx,
  teacherFilterHeaderSx,
  teacherFilterPanelSx,
  teacherFilterTitleSx,
  teacherFilterToggleSx,
  teacherFilterTopStackSx,
} from '../../styles/teacherFilterStyles';

const TestsPage = () => {
  const { user } = useAuth();
  
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  const [tests, setTests] = useState([]);
  const [rawTests, setRawTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ year:'', subject:'', topic:'', status:'', type:'', tags:'' });
  const [selfOnly, setSelfOnly] = useState(false);
  const [openAutoDialog, setOpenAutoDialog] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [generatedTestId, setGeneratedTestId] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [pendingGeneratedTestData, setPendingGeneratedTestData] = useState(null);
  const [shouldCreateAfterFormUpdate, setShouldCreateAfterFormUpdate] = useState(false);
  const initialForm = {
    my_questions: true,
    title: '',
    subject: '',
    topics: [],
    test_type: TEST_TYPES.ASSESSMENT,
    board: '',
    academic_year: '',
    num_questions: 10,
    duration_minutes: 60,
    percent_basic: 50,
    percent_advanced: 50,
    note: '',
  };
  const [questionsDialog,setQuestionsDialog]=useState({open:false,questions:[],test:null});
  const [form, setForm] = useState(initialForm);
  const [myQuestions, setMyQuestions] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSuccess = (msg) => setSnackbar({ open: true, message: msg, severity: 'success' });
  const showError = (msg) => setSnackbar({ open: true, message: msg, severity: 'error' });
  const [subjects, setSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [years, setYears] = useState([]);
  const [boards, setBoards] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [manualTestDialogOpen, setManualTestDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mobileCardMenu, setMobileCardMenu] = useState({ anchor: null, test: null });
  const [tagDialog, setTagDialog] = useState({ open: false, test: null, tags: [], saving: false });

  const buildQuery = () => {
    const params = [];
    // Always send mine parameter to be explicit
    params.push(`mine=${selfOnly ? 'true' : 'false'}`);
    if (filters.year) params.push(`academic_year=${filters.year}`);
    if (filters.subject) params.push(`subject=${filters.subject}`);
    if (filters.topic) params.push(`topic=${filters.topic}`);
    if (filters.status) params.push(`status=${filters.status}`);
    if (filters.type) params.push(`test_type=${filters.type}`);
    if (filters.tags) params.push(`tags=${filters.tags}`);
    return params.length?`?${params.join('&')}`:'';
  };

  const fetchTests = async () => {
    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }
    
    try {
      setLoading(true);
      const query = buildQuery();
      
      const response = await api.get(`${TESTS}${query}`);
      
      const raw = Array.isArray(response.data) ? response.data : (response.data.results || []);
      // Filter out revision tests from raw data
      const filteredRaw = raw.filter(test => test.test_type !== TEST_TYPES.REVISE);
      setRawTests(filteredRaw);
      
      const processed = filteredRaw
        .map(test => ({
        ...test,
        id: test.id,
        topics_display: test.topics_list?.length > 0 ? test.topics_list.join(', ') : 'No topics',
        board_name: test.board?.name || test.board_name || 'N/A',
        year_name: test.academic_year?.year || test.year_name || 'N/A',
        percent_basic: test.percent_basic || 0,
        percent_advanced: test.percent_advanced || 0,
        num_questions: test.num_questions || 0,
        duration_minutes: test.duration_minutes || 0,
      }));
      // Note: Backend now automatically filters out tests created by students
      // Only tests created by teachers (both self and others) are returned
      setTests(processed);
    } catch (error) {

      showError('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchLOVs = async () => {
    try {
      const [subRes, yearRes, boardRes, topicRes, difficultiesRes, tagRes] = await Promise.all([
        api.get(SUBJECTS),
        api.get(ACADEMIC_YEARS + '?published_only=true'),
        api.get(BOARDS_EXAMS + '?published_only=true'),
        api.get(SUBJECT_TOPICS),
        api.get(DIFFICULTIES + '?page_size=100'),
        api.get(META_TAGS + '?is_active=true&page_size=1000'),
      ]);
      
      setSubjects(Array.isArray(subRes.data) ? subRes.data : subRes.data.results || []);
      setYears(filterPublished(Array.isArray(yearRes.data) ? yearRes.data : yearRes.data.results || []));
      setBoards(filterPublished(Array.isArray(boardRes.data) ? boardRes.data : boardRes.data.results || []));
      setAllTopics(Array.isArray(topicRes.data) ? topicRes.data : topicRes.data.results || []);
      setTopicOptions(Array.isArray(topicRes.data) ? topicRes.data : topicRes.data.results || []);
      setDifficulties(difficultiesRes.data?.results || difficultiesRes.data || []);
      setAvailableTags(Array.isArray(tagRes.data) ? tagRes.data : tagRes.data.results || []);
    } catch (error) {
      // Silently handle - boards/years may be empty if API fails
    }
  };

  useEffect(() => {
    fetchLOVs();
  }, []);

  // Fetch tests on mount and whenever filters or selfOnly change so the list reflects current selection
  useEffect(() => {
    fetchTests();
  }, [
    selfOnly,
    filters.year,
    filters.subject,
    filters.topic,
    filters.status,
    filters.type,
    filters.tags,
  ]);

  // Watch for form update flag and call handleCreate
  useEffect(() => {
    if (shouldCreateAfterFormUpdate) {
      setShouldCreateAfterFormUpdate(false); // Reset the flag
      handleCreate(); // Now call handleCreate with updated form state
    }
  }, [shouldCreateAfterFormUpdate]);

  const handleManualTestSave = async (testData) => {
    try {
      setSaving(true);
      // The test has already been created by the ManualTestDialog component
      // We just need to handle the success case and refresh the tests list
      showSuccess('Test created successfully');
      setManualTestDialogOpen(false);
      // Clear filters and refresh tests in one operation to reduce flickering
      setFilters({ year:'', subject:'', topic:'', status:'', type:'', tags:'' });
      setSelfOnly(false);
      // Use a shorter delay to reduce flickering
      setTimeout(() => {
        fetchTests();
      }, 200);
    } catch (error) {

      showError(error.message || 'Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePercentChange = (field) => (e) => {
    const value = parseInt(e.target.value) || 0;
    setForm(prev => {
      const otherField = field === 'percent_basic' ? 'percent_advanced' : 'percent_basic';
      const otherValue = prev[otherField] || 0;
      const newOtherValue = Math.max(0, 100 - value);
      
      return {
        ...prev,
        [field]: value,
        [otherField]: newOtherValue
      };
    });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${TESTS}${id}/`);
      fetchTests();
      showSuccess('Test deleted');
    } catch (e) {
      showError(e.message);
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.patch(`${TESTS}${id}/`, { status: 'PUBLISHED' });
      fetchTests();
      showSuccess('Test published');
    } catch (e) {
      showError(e.message);
    }
  };

  const handleArchive = async (id) => {
    try {
      await api.patch(`${TESTS}${id}/`, { status: 'ARCHIVED' });
      fetchTests();
      showSuccess('Test archived');
    } catch (e) {
      showError(e.message);
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await api.patch(`${TESTS}${id}/`, { status: 'PUBLISHED' });
      fetchTests();
      showSuccess('Test unarchived');
    } catch (e) {
      showError(e.message);
    }
  };

  const handleView = async (row) => {
    try {
      const { test, questions } = await testService.loadTestQuestionsForView(row.id);
      setQuestionsDialog({
        open: true,
        questions,
        test: { ...row, ...test }
      });
    } catch (e) {
      showError('Failed to load test questions');
    }
  };

  const getTestTagIds = (test) => {
    if (!test) return [];
    if (Array.isArray(test.tags)) {
      return test.tags
        .map((tag) => (typeof tag === 'number' ? tag : tag?.id))
        .filter((id) => typeof id === 'number' && id > 0);
    }
    if (Array.isArray(test.tags_detail)) {
      return test.tags_detail
        .map((tag) => tag?.id)
        .filter((id) => typeof id === 'number' && id > 0);
    }
    return [];
  };

  const openTagDialog = (test) => {
    setTagDialog({
      open: true,
      test,
      tags: getTestTagIds(test),
      saving: false,
    });
  };

  const closeTagDialog = () => {
    setTagDialog({ open: false, test: null, tags: [], saving: false });
  };

  const handleSaveTestTags = async () => {
    if (!tagDialog.test || tagDialog.saving) return;
    setTagDialog((prev) => ({ ...prev, saving: true }));
    try {
      const tagIds = await processTagPayload(tagDialog.tags);
      const response = await api.patch(`${TESTS}${tagDialog.test.id}/`, { tags: tagIds });
      const updatedTest = response.data || {};
      const nextTags = Array.isArray(updatedTest.tags) ? updatedTest.tags : tagIds;
      const nextTagsDetail = Array.isArray(updatedTest.tags_detail)
        ? updatedTest.tags_detail
        : availableTags.filter((tag) => tagIds.some((id) => String(id) === String(tag.id)));

      const updateRow = (test) =>
        String(test.id) === String(tagDialog.test.id)
          ? { ...test, ...updatedTest, tags: nextTags, tags_detail: nextTagsDetail }
          : test;

      setTests((prev) => prev.map(updateRow));
      setRawTests((prev) => prev.map(updateRow));
      showSuccess('Test tags updated');
      closeTagDialog();
      fetchLOVs();
    } catch (error) {
      showError(error.message || 'Failed to update test tags');
      setTagDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleFilterChange = (field) => (e) => {
    const val = e.target.value;
    const numericFields = ['year'];
    setFilters(prev => {
      const next = { ...prev, [field]: numericFields.includes(field) && val !== '' ? Number(val) : val };
      if (field === 'subject') next.topic = '';
      return next;
    });
  };

  /** Topics available in the Topic filter (scoped by subject when selected) */
  const filterTopicOptions = useMemo(() => {
    if (!filters.subject || !Array.isArray(topicOptions)) return topicOptions || [];
    const subId = Number(filters.subject);
    return topicOptions.filter(t => {
      const sub = t.subject ?? t.subject_id ?? (t.subject && t.subject.id);
      return Number(sub) === subId;
    });
  }, [filters.subject, topicOptions]);

  const clearFilters = () => {
    setFilters({ year:'', subject:'', topic:'', status:'', type:'', tags:'' });
    setSelfOnly(false);
    // Refresh tests after clearing filters
    setTimeout(() => {
      fetchTests();
    }, 100);
  };

  const validateForm=()=>{
    if(!form.title) return 'Title required';
    if(!form.test_type) return 'Test type required';
    if(!form.subject) return 'Subject required';
    if(!form.board) return 'Board required';
    if(!form.academic_year) return 'Academic year required';
    if(!form.topics || form.topics.length===0) return 'Select at least one topic';
    if(form.num_questions<=0) return 'Question count >0';
    return null;
  };

  const handleCreate = async () => {
    const err=validateForm();
    if(err){showError(err);return;}

    const totalPct = form.percent_basic + form.percent_advanced;
    if (totalPct !== 100) {
      showError('Percentages must sum to 100');
      return;
    }
    try {
      // Extract topic IDs from topic objects
      const topicIds = form.topics.map(topic => typeof topic === 'object' ? topic.id : topic);
      
      
      const payload = {
        ...form,
        topics: topicIds, // Use the extracted IDs instead of full objects
        my_questions: myQuestions,
        percent_basic: form.percent_basic,
        percent_advanced: form.percent_advanced,
      };
      
      
              const res = await api.post(TESTS_GENERATE, payload);
      if (res.status === 201) {
        const testId = res.data?.id;
        try {
          const questionsResponse = await api.get(`${TESTS}${testId}/questions/`);
          const questions = Array.isArray(questionsResponse.data)
            ? questionsResponse.data
            : (questionsResponse.data?.results || []);
          if (!questions.length) {
            throw new Error('No generated questions were returned for review.');
          }
          setGeneratedTestId(testId);
          setGeneratedQuestions(questions);
          setPendingGeneratedTestData(payload);
          setOpenAutoDialog(false);
          setReviewDialogOpen(true);
        } catch (reviewError) {
          try {
            await api.delete(`${TESTS}${testId}/`);
          } catch (cleanupError) {
            console.error('Failed to remove unreviewed generated test', cleanupError);
          }
          throw new Error(reviewError.message || 'Could not load generated questions for review.');
        }
      } else {
        showError(JSON.stringify(res.data));
      }
    } catch (e) {
      
      showError(e.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PUBLISHED': return 'success';
      case 'DRAFT': return 'warning';
      case 'ARCHIVED': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'PUBLISHED': return 'Published';
      case 'DRAFT': return 'Draft';
      case 'ARCHIVED': return 'Archived';
      default: return status || 'Unknown';
    }
  };

  const columns = [
    { 
      field: 'actions', 
      headerName: 'Actions', 
      width: 210, 
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View Test">
            <IconButton 
              size="small" 
              onClick={() => handleView(params.row)}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.50' }
              }}
            >
              <VisibilityIcon fontSize="small"/>
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Tags">
            <IconButton
              size="small"
              onClick={() => openTagDialog(params.row)}
              sx={{
                color: 'secondary.main',
                '&:hover': { backgroundColor: 'secondary.50' }
              }}
            >
              <LocalOfferIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'DRAFT' && (
            <Tooltip title={(() => {
              const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
              return isOwner ? "Publish Test" : "Cannot publish – not owner";
            })()}>
              <span>
                <IconButton 
                  size="small" 
                  color="success" 
                  disabled={(() => {
                    const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
                    return !isOwner;
                  })()}
                  onClick={() => handlePublish(params.row.id)}
                  sx={{ 
                    '&:hover': { backgroundColor: 'success.50' },
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {params.row.status === 'PUBLISHED' && (
            <Tooltip title={(() => {
              const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
              return isOwner ? "Archive Test" : "Cannot archive – not owner";
            })()}>
              <span>
                <IconButton 
                  size="small" 
                  color="warning" 
                  disabled={(() => {
                    const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
                    return !isOwner;
                  })()}
                  onClick={() => handleArchive(params.row.id)}
                  sx={{ 
                    '&:hover': { backgroundColor: 'warning.50' },
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {params.row.status === 'ARCHIVED' && (
            <Tooltip title={(() => {
              const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
              return isOwner ? "Unarchive Test" : "Cannot unarchive – not owner";
            })()}>
              <span>
                <IconButton 
                  size="small" 
                  color="info" 
                  disabled={(() => {
                    const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
                    return !isOwner;
                  })()}
                  onClick={() => handleUnarchive(params.row.id)}
                  sx={{ 
                    '&:hover': { backgroundColor: 'info.50' },
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <UnarchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {(() => {
            const isOwner = params.row.created_by === user?.id || params.row.created_by_id === user?.id;
            const isAssigned = (params.row.assignment_count || 0) > 0;
            
            let canDelete = false;
            let tooltipText = '';
            
            if (!isOwner) {
              canDelete = false;
              tooltipText = 'Cannot delete – not owner';
            } else if (isAssigned) {
              canDelete = false;
              tooltipText = 'Cannot delete – test assigned';
            } else {
              canDelete = true;
              tooltipText = 'Delete Test';
            }
            
            return (
              <Tooltip title={tooltipText}>
                <span>
                  <IconButton 
                    size="small" 
                    color="error" 
                    disabled={!canDelete} 
                    onClick={canDelete ? () => handleDelete(params.row.id) : undefined}
                    sx={{ 
                      '&:hover': { backgroundColor: 'error.50' },
                      '&.Mui-disabled': { opacity: 0.3 }
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          })()}
        </Stack>
      )
    },
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      align: 'center',
      headerAlign: 'center'
    },
    { 
      field: 'title', 
      headerName: 'Title', 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value} placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={getStatusLabel(params.value)}
          size="small"
          color={getStatusColor(params.value)}
          variant="outlined"
          sx={{ 
            textTransform: 'capitalize',
            minWidth: 80,
            fontWeight: 500
          }}
        />
      )
    },
    { 
      field: 'topics_display', 
      headerName: 'Topics', 
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Tooltip title={params.value} placement="top">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    { 
      field: 'percent_basic', 
      headerName: '% Basic', 
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderHeader: (params) => (
        <Tooltip title="Percentage of Basic-level questions" placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            % Basic
          </Typography>
        </Tooltip>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}%
        </Typography>
      )
    },
    { 
      field: 'percent_advanced', 
      headerName: '% Adv', 
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderHeader: (params) => (
        <Tooltip title="Percentage of Advanced-level questions" placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            % Adv
          </Typography>
        </Tooltip>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}%
        </Typography>
      )
    },
    { 
      field: 'num_questions', 
      headerName: '# Qns', 
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderHeader: (params) => (
        <Tooltip title="Number of questions" placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            # Qns
          </Typography>
        </Tooltip>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'board_name', 
      headerName: 'Board', 
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'year_name', 
      headerName: 'Year', 
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'duration_minutes', 
      headerName: 'Duration', 
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value} min
        </Typography>
      )
    }
  ];

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  
  const typeOptions = useMemo(()=>{
    // Create options using TEST_TYPE_OPTIONS for consistent labels, excluding revision tests
    // Don't depend on rawTests data since we filter it out
    const options = TEST_TYPE_OPTIONS
      .filter(option => option.value !== TEST_TYPES.REVISE) // Exclude revision tests
      .map(option => ({
        value: option.value,
        label: option.label
      }));
    
    return options;
  }, []);

  const filteredTests = useMemo(() => {
    return tests.filter(test => {
      // Exclude revision tests from the main list
      if (test.test_type === TEST_TYPES.REVISE) return false;
      
      // Apply type filter
      if (filters.type && String(test.test_type) !== String(filters.type)) return false;

      if (filters.tags) {
        const tagIds = Array.isArray(test.tags)
          ? test.tags
          : Array.isArray(test.tags_detail)
            ? test.tags_detail.map((tag) => tag.id)
            : [];
        if (!tagIds.some((id) => String(id) === String(filters.tags))) return false;
      }
      
      return true;
    });
  }, [tests, filters.type, filters.tags]);

  const isOwner = (test) => test && (test.created_by === user?.id || test.created_by_id === user?.id);
  const canDeleteTest = (test) => isOwner(test) && !((test.assignment_count || 0) > 0);

  const renderMobileTestCards = () =>
    filteredTests.map((test) => {
      const openMenu = (e) => {
        e.stopPropagation();
        setMobileCardMenu({ anchor: e.currentTarget, test });
      };
      const closeMenu = () => setMobileCardMenu({ anchor: null, test: null });
      return (
        <Paper
          key={test.id}
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            '&:last-child': { mb: 0 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Chip label={`#T-${test.id}`} size="small" sx={{ fontWeight: 600, flexShrink: 0 }} />
            <IconButton size="small" onClick={openMenu} aria-label="More actions">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
          <Tooltip title={test.title} placement="top">
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {test.title}
            </Typography>
          </Tooltip>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip label={getStatusLabel(test.status)} size="small" color={getStatusColor(test.status)} variant="outlined" sx={{ textTransform: 'capitalize' }} />
            {test.topics_display && test.topics_display !== 'No topics' && (
              <Chip label={test.topics_display} size="small" variant="outlined" sx={{ maxWidth: 160, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }} } />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="View Test">
              <IconButton size="small" onClick={() => handleView(test)} sx={{ color: 'primary.main' }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {test.status === 'DRAFT' && (
              <Tooltip title={isOwner(test) ? 'Publish Test' : 'Cannot publish – not owner'}>
                <span>
                  <IconButton size="small" color="success" disabled={!isOwner(test)} onClick={() => handlePublish(test.id)}>
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {test.status === 'PUBLISHED' && (
              <Tooltip title={isOwner(test) ? 'Archive Test' : 'Cannot archive – not owner'}>
                <span>
                  <IconButton size="small" color="warning" disabled={!isOwner(test)} onClick={() => handleArchive(test.id)}>
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {test.status === 'ARCHIVED' && (
              <Tooltip title={isOwner(test) ? 'Unarchive Test' : 'Cannot unarchive – not owner'}>
                <span>
                  <IconButton size="small" color="info" disabled={!isOwner(test)} onClick={() => handleUnarchive(test.id)}>
                    <UnarchiveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={canDeleteTest(test) ? 'Delete Test' : (isOwner(test) ? 'Cannot delete – test assigned' : 'Cannot delete – not owner')}>
              <span>
                <IconButton size="small" color="error" disabled={!canDeleteTest(test)} onClick={canDeleteTest(test) ? () => handleDelete(test.id) : undefined}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      );
    });

  const FilterPanel = ({ variant = 'side' }) => {
    const isTop = variant === 'top';

    return (
    <Paper 
      elevation={2} 
      sx={teacherFilterPanelSx(isTop, 0)}
    >
      <Box sx={teacherFilterHeaderSx(isTop)}>
        <Typography
          variant={isTop ? 'caption' : 'h6'}
          sx={teacherFilterTitleSx(isTop)}
        >
          {isTop ? 'Test filters' : 'Filters'}
        </Typography>
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={clearFilters}
          disabled={!Object.values(filters).some(v => v !== '')}
          sx={teacherFilterClearButtonSx}
        >
          Clear All
        </Button>
      </Box>
      
      <Stack
        spacing={isTop ? 1.5 : 3}
        direction={isTop ? 'row' : 'column'}
        useFlexGap
        flexWrap={isTop ? 'wrap' : 'nowrap'}
        alignItems={isTop ? 'center' : 'stretch'}
        sx={teacherFilterTopStackSx(isTop ? { md: 170, lg: 190 } : '100%')}
      >
        {/* Toggle switch with dynamic label */}
        <FormControlLabel 
          control={
            <Switch 
              checked={selfOnly} 
              onChange={(e) => setSelfOnly(e.target.checked)}
              color="primary"
            />
          } 
          label={selfOnly ? "My Tests Only" : "All Tests"}
          sx={teacherFilterToggleSx(isTop)}
        />
        
        <TextField 
          select 
          label="Status" 
          size="small" 
          value={filters.status} 
          onChange={handleFilterChange('status')}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="PUBLISHED">Published</MenuItem>
          <MenuItem value="ARCHIVED">Archived</MenuItem>
        </TextField>
        
        <TextField 
          select 
          label="Academic Year" 
          size="small" 
          value={filters.year} 
          onChange={handleFilterChange('year')}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All Years</MenuItem>
          {Array.isArray(years) && years.map(y => (
            <MenuItem key={y.id} value={y.id}>{y.label||y.year}</MenuItem>
          ))}
        </TextField>
        
        <TextField 
          select 
          label="Subject" 
          size="small" 
          value={filters.subject} 
          onChange={handleFilterChange('subject')}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All Subjects</MenuItem>
          {Array.isArray(subjects) && subjects.map(s=> (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
        
        <Autocomplete
          size="small"
          options={filterTopicOptions}
          getOptionLabel={(option) => option.topic || option.name || ''}
          value={filterTopicOptions.find((t) => String(t.id) === String(filters.topic)) || null}
          onChange={(e, newValue) =>
            setFilters((prev) => ({ ...prev, topic: newValue ? newValue.id : '' }))
          }
          isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Topic"
              placeholder="Search or select topic"
              sx={teacherFilterFieldSx}
            />
          )}
          noOptionsText="No topics found"
        />

        <TextField 
          select 
          label="Type" 
          size="small" 
          value={filters.type} 
          onChange={handleFilterChange('type')}
          sx={teacherFilterFieldSx}
        >
          <MenuItem value="">All Types</MenuItem>
          {typeOptions.map(type=>(
            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
          ))}
        </TextField>

        <Autocomplete
          size="small"
          options={availableTags}
          getOptionLabel={(option) => option.name || ''}
          value={availableTags.find((tag) => String(tag.id) === String(filters.tags)) || null}
          onChange={(e, newValue) =>
            setFilters((prev) => ({ ...prev, tags: newValue ? newValue.id : '' }))
          }
          isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tag"
              placeholder="Search or select tag"
              sx={teacherFilterFieldSx}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.75,
                  bgcolor: option.color || 'primary.main',
                  border: '1px solid',
                  borderColor: 'divider',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              {option.name}
            </Box>
          )}
          noOptionsText="No tags found"
        />
      </Stack>
    </Paper>
    );
  };

  const CustomToolbar = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      mb: 2,
      p: 1,
      flexWrap: 'wrap',
      gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton 
          sx={{ 
            color: 'primary.main',
            '&:hover': { backgroundColor: 'primary.50' }
          }}
        />
        <GridToolbarDensitySelector 
          sx={{ 
            color: 'primary.main',
            '&:hover': { backgroundColor: 'primary.50' }
          }}
        />
      </Box>
    </Box>
  );

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      textAlign: 'center'
    }}>
      <Box sx={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        No tests found
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Try adjusting your filters or create your first test.
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <FilterPanel />
        </Box>
      </Drawer>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowX: 'hidden', minWidth: 0 }}>
        {/* Desktop Layout – align filter card to top of main content */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <FilterPanel variant="top" />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 3,
                flexWrap: 'wrap',
                gap: 1.5
              }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    Manage Tests
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Total Tests: {filteredTests.length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => {
                      setForm(initialForm);
                      setOpenAutoDialog(true);
                    }}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    Auto-Generate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setManualTestDialogOpen(true)}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    Manual Select
                  </Button>
                </Box>
              </Box>

              {/* Table */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : filteredTests.length === 0 ? (
                <EmptyState />
              ) : (
                <Box sx={{ overflowX: 'auto', minWidth: 0, width: '100%' }}>
                <DataGrid 
                  rows={filteredTests}
                  columns={columns} 
                  pagination
                  paginationModel={paginationModel}
                  onPaginationModelChange={(model)=>setPaginationModel(model)}
                  pageSizeOptions={[10,25,50,100]}
                  rowsPerPageOptions={[10,25,50,100]}
                  sortModel={[{ field:'id', sort:'desc' }]} 
                  autoHeight 
                  components={{ Toolbar: CustomToolbar }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-row': {
                      '&:nth-of-type(even)': {
                        backgroundColor: 'grey.50'
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      fontWeight: 600
                    }
                  }}
                />
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Mobile Layout */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: 0 }}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              borderRadius: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <SummaryCard 
              label="Total Tests" 
              value={filteredTests.length} 
              icon={AssignmentIcon} 
            />

            {/* Horizontally scrollable action bar */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1,
                mb: 2,
                flexShrink: 0,
                '&::-webkit-scrollbar': { height: 6 },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFilterOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Filters
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => {
                  setForm(initialForm);
                  setOpenAutoDialog(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Auto-Generate
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setManualTestDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Manual Select
              </Button>
            </Box>

            {/* Mobile card list */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredTests.length === 0 ? (
              <EmptyState />
            ) : (
              <Box>
                {renderMobileTestCards()}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Mobile card kebab menu */}
        <Menu
          anchorEl={mobileCardMenu.anchor}
          open={Boolean(mobileCardMenu.anchor)}
          onClose={() => setMobileCardMenu({ anchor: null, test: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {mobileCardMenu.test?.status === 'DRAFT' && isOwner(mobileCardMenu.test) && (
            <MenuItem onClick={() => { handlePublish(mobileCardMenu.test.id); setMobileCardMenu({ anchor: null, test: null }); }}>
              Publish Test
            </MenuItem>
          )}
          {mobileCardMenu.test?.status === 'PUBLISHED' && isOwner(mobileCardMenu.test) && (
            <MenuItem onClick={() => { handleArchive(mobileCardMenu.test.id); setMobileCardMenu({ anchor: null, test: null }); }}>
              Archive Test
            </MenuItem>
          )}
          {mobileCardMenu.test?.status === 'ARCHIVED' && isOwner(mobileCardMenu.test) && (
            <MenuItem onClick={() => { handleUnarchive(mobileCardMenu.test.id); setMobileCardMenu({ anchor: null, test: null }); }}>
              Unarchive Test
            </MenuItem>
          )}
          {mobileCardMenu.test && (
            <MenuItem onClick={() => { openTagDialog(mobileCardMenu.test); setMobileCardMenu({ anchor: null, test: null }); }}>
              Edit Tags
            </MenuItem>
          )}
          {mobileCardMenu.test && canDeleteTest(mobileCardMenu.test) && (
            <MenuItem onClick={() => { handleDelete(mobileCardMenu.test.id); setMobileCardMenu({ anchor: null, test: null }); }} sx={{ color: 'error.main' }}>
              Delete Test
            </MenuItem>
          )}
        </Menu>

      </Box>

      {/* Auto-Generate Test Dialog */}
      <AutoTestDialog
        open={openAutoDialog}
        onClose={() => setOpenAutoDialog(false)}
        onSave={(formData) => {
          
          // Check if this is an error response
          if (formData.error) {
            showError(formData.error);
            return; // Don't proceed with form update or creation
          }
          
          // This is valid form data, proceed with normal flow
          
          // Update the local form state with the form data from AutoTestDialog
          const updatedForm = {
            ...formData,
            my_questions: formData.my_questions || false
          };
          
          // Update the form state
          setForm(updatedForm);
          
          // Also update the myQuestions state to keep them in sync
          setMyQuestions(formData.my_questions || false);
          
          // Set the flag to trigger handleCreate after form update
          setShouldCreateAfterFormUpdate(true);
        }}
        subjects={subjects}
        years={years}
        boards={boards}
        topics={allTopics}
        initialForm={{
          ...form,
          my_questions: myQuestions // Map the teacher's myQuestions state to my_questions
        }}
        showMyQuestions={true}
        title="Auto-Generate Test"
        saveButtonText="Generate"
        user={user}
      />

      <TestQuestionReviewDialog
        open={reviewDialogOpen}
        onClose={async () => {
          const testId = generatedTestId;
          setReviewDialogOpen(false);
          setGeneratedTestId(null);
          setGeneratedQuestions([]);
          setPendingGeneratedTestData(null);
          if (testId) {
            try {
              await api.delete(`${TESTS}${testId}/`);
            } catch (cleanupError) {
              console.error('Failed to discard unreviewed generated test', cleanupError);
              showError('Could not discard the unreviewed draft test. Please remove it from the test list.');
            }
          }
        }}
        testId={generatedTestId}
        questions={generatedQuestions}
        testData={pendingGeneratedTestData}
        requireCompletionDate={false}
        confirmLabel="Save Test"
        onAssign={async () => {
          setReviewDialogOpen(false);
          setGeneratedTestId(null);
          setGeneratedQuestions([]);
          setPendingGeneratedTestData(null);
          showSuccess('Test created');
          setFilters({ year:'', subject:'', topic:'', status:'', type:'', tags:'' });
          setSelfOnly(false);
          setTimeout(() => fetchTests(), 200);
        }}
      />

      {/* Manual Test Dialog */}
      <ManualTestDialog
        open={manualTestDialogOpen}
        onClose={() => setManualTestDialogOpen(false)}
        onSave={handleManualTestSave}
        saving={saving}
        subjects={subjects}
        years={years}
        boards={boards}
        topics={allTopics}
        difficulties={difficulties}
      />

      <ViewTestQuestionsDialog
        open={questionsDialog.open}
        onClose={() => setQuestionsDialog({ open: false, questions: [], test: null })}
        test={questionsDialog.test}
        questions={questionsDialog.questions}
        onMessage={(msg, severity) => (severity === 'success' ? showSuccess(msg) : showError(msg))}
      />

      <Dialog open={tagDialog.open} onClose={tagDialog.saving ? undefined : closeTagDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Test Tags</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {tagDialog.test?.title || 'Selected test'}
            </Typography>
            <TagSelector
              value={tagDialog.tags}
              onChange={(tags) => setTagDialog((prev) => ({ ...prev, tags }))}
              label="Tags"
              helperText="Search existing tags or press Enter to create a new tag"
              disabled={tagDialog.saving}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeTagDialog} disabled={tagDialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTestTags}
            variant="contained"
            disabled={tagDialog.saving}
            startIcon={tagDialog.saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TestsPage;
